import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import { SectionHead } from '../../PipelineComponents';
import PivotCard from './PivotCard';
import SummaryCard from './SummaryCard';
import { buildPivot } from './pivotHelpers';

const Pivots = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [summary, setSummary] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getIehpReport(params);

        setSummary(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const {
    membersPerLob, finalOutcome, status, actionNeeded, sourceTotals, outcomesByCategory,
  } = useMemo(() => ({
    membersPerLob: buildPivot(summary?.membersByMonth, { accumulateData: true, colKey: 'lob' }),

    finalOutcome: buildPivot(summary?.outcomesByMonth, { withTotalRow: true, sortByMonth: true, colKey: 'finalOutcome' }),

    status: buildPivot(summary?.statusByMonth, { withTotalRow: true, sortByMonth: true, colKey: 'status' }),

    actionNeeded: buildPivot(summary?.actionByMonth, { accumulateData: true, colKey: 'actionNeededBy' }),

    sourceTotals: buildPivot(summary?.outcomeBySource, { accumulateData: true }),

    outcomesByCategory: buildPivot(summary?.outcomeBySource, { accumulateData: true }),
  }), [summary]);

  const loadingProps = { loading, firstLoad: firstLoad.current };

  return (
    <>
      {!!section && <SectionHead section={section} />}

      <div className={classes.row}>
        <PivotCard
          title="Count of Member's per LOB"
          caption="Members by line of business"
          pivot={membersPerLob}
          chartProps={{
            axis: 'column', totals: true, distributed: true, showLegend: true,
          }}
          {...loadingProps}
        />
      </div>

      <div className={classes.row}>
        <PivotCard
          title="Count per Final Outcome by LOB per Month"
          caption="Every final outcome, month by month"
          pivot={finalOutcome}
          tableProps={{ firstColTitle: 'Month', showTotalCol: true }}
          chartProps={{ withTotal: true, showLegend: true }}
          height={350}
          {...loadingProps}
        />
      </div>

      <div className={classes.row}>
        <PivotCard
          title="Count of Status per Month"
          caption="Pipeline status, month by month"
          pivot={status}
          tableProps={{ firstColTitle: 'Month', showTotalCol: true }}
          chartProps={{ withTotal: true, showLegend: true }}
          height={330}
          {...loadingProps}
        />
      </div>

      <div className={classes.row}>
        <PivotCard
          title="Count of Action Needed"
          caption="Who the next action sits with"
          pivot={actionNeeded}
          chartProps={{
            axis: 'column', totals: true, distributed: true, showLegend: true,
          }}
          {...loadingProps}
        />
      </div>

      <div className={classes.row}>
        <PivotCard
          title="Final Outcome Totals per Month"
          caption="Totals by source for the selected period"
          pivot={sourceTotals}
          chartProps={{
            axis: 'column', totals: true, distributed: true, showLegend: true,
          }}
          height={330}
          {...loadingProps}
        />
      </div>

      <div className={classes.row}>
        <PivotCard
          title="Final Outcomes by Category"
          caption="Where the members came from"
          pivot={outcomesByCategory}
          tableProps={{ titles: ['Source', 'Count of Final Outcome'] }}
          chartProps={{ totals: true, distributed: true, showLegend: true }}
          height={340}
          {...loadingProps}
        />
      </div>

      <div className={classes.row}>
        <SummaryCard params={params} />
      </div>
    </>
  );
};

Pivots.propTypes = {
  section: PropTypes.object,
  params: PropTypes.object.isRequired,
};

export default Pivots;
